/**
 * Modal Avançado de Gerenciamento de Arquivos
 * 
 * Modal reutilizável com design glassmorphism para gerenciamento de arquivos
 * Baseado no modal existente em ProjectsPage.tsx
 * 
 * @author Equipe GestaoeCRM
 * @date 25/10/2025
 */

import React, { useState, useEffect } from 'react';
import { 
  X, 
  Upload, 
  Download, 
  Trash2, 
  File, 
  FileText, 
  Image, 
  Video, 
  Archive,
  Folder,
  Plus,
  Shield,
  Eye,
  EyeOff
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

interface FileItem {
  id: string;
  originalName: string;
  fileSize: number;
  isFolder: boolean;
  isPublic: boolean;
  editable: boolean;
  source: string;
  uploader: {
    name: string;
  };
  createdAt: string;
  mimeType?: string;
}

interface AdvancedFileManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (files: string[]) => void;
  demandId?: string;
  projectId?: string;
  title: string;
  entityType: 'demand' | 'project' | 'os';
  entityInfo?: {
    name: string;
    status?: string;
    priority?: string;
    client?: string;
    description?: string;
  };
}

const AdvancedFileManagerModal: React.FC<AdvancedFileManagerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  demandId,
  projectId,
  title,
  entityType,
  entityInfo
}) => {
  const { user } = useAuth();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);

  // Carregar arquivos existentes
  useEffect(() => {
    if (isOpen && (demandId || projectId)) {
      loadFiles();
    }
  }, [isOpen, demandId, projectId]);

  const loadFiles = async () => {
    if (!demandId && !projectId) return;

    try {
      setLoading(true);
      let response;
      
      if (demandId) {
        response = await api.get(`/demand-files/${demandId}/files`);
      } else if (projectId) {
        response = await api.get(`/project-files/${projectId}/files`);
      }
      
      if (response?.data.success) {
        setFiles(response.data.data || []);
      } else {
        setFiles([]);
      }
    } catch (error) {
      // console.error('Erro ao carregar arquivos:', error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    setSelectedFiles(prev => [...prev, ...selected]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    setSelectedFiles(prev => [...prev, ...droppedFiles]);
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if ((!demandId && !projectId) || selectedFiles.length === 0) return;

    try {
      setUploading(true);
      
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('files', file);
        
        let endpoint;
        if (demandId) {
          endpoint = `/demand-files/${demandId}/files/upload`;
        } else if (projectId) {
          endpoint = `/project-files/${projectId}/files/upload`;
        }
        
        await api.post(endpoint!, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      // Recarregar arquivos após upload
      await loadFiles();
      setSelectedFiles([]);
      toast.success('Arquivos enviados com sucesso!');
      
    } catch (error) {
      // console.error('Erro ao fazer upload:', error);
      toast.error('Erro ao fazer upload dos arquivos. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (file: FileItem) => {
    try {
      let endpoint;
      if (demandId) {
        endpoint = `/demand-files/${demandId}/files/${file.id}/download`;
      } else if (projectId) {
        endpoint = `/project-files/${projectId}/files/${file.id}/download`;
      }
      
      const response = await api.get(endpoint!, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error('Erro ao fazer download:', error);
      toast.error('Erro ao fazer download do arquivo.');
    }
  };

  const deleteFile = async (fileId: string) => {
    if (!confirm('Tem certeza que deseja excluir este arquivo?')) return;

    try {
      let endpoint;
      if (demandId) {
        endpoint = `/demand-files/${demandId}/files/${fileId}`;
      } else if (projectId) {
        endpoint = `/project-files/${projectId}/files/${fileId}`;
      }
      
      await api.delete(endpoint!);
      await loadFiles();
      toast.success('Arquivo excluído com sucesso!');
    } catch (error) {
      // console.error('Erro ao excluir arquivo:', error);
      toast.error('Erro ao excluir arquivo. Tente novamente.');
    }
  };

  const changeFileVisibility = async (file: FileItem, isPublic: boolean) => {
    try {
      let endpoint;
      if (demandId) {
        endpoint = `/demand-files/${demandId}/files/${file.id}/visibility`;
      } else if (projectId) {
        endpoint = `/project-files/${projectId}/files/${file.id}/visibility`;
      }
      
      await api.patch(endpoint!, { isPublic });
      await loadFiles();
      toast.success(`Arquivo ${isPublic ? 'tornado público' : 'tornado privado'} com sucesso!`);
    } catch (error) {
      // console.error('Erro ao alterar visibilidade:', error);
      toast.error('Erro ao alterar visibilidade do arquivo.');
    }
  };

  const getFileIcon = (file: FileItem) => {
    if (file.isFolder) return <Folder className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />;
    
    const mimeType = file.mimeType || '';
    if (mimeType.startsWith('image/')) return <Image className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />;
    if (mimeType.startsWith('video/')) return <Video className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />;
    if (mimeType.includes('zip') || mimeType.includes('rar')) return <Archive className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />;
    if (mimeType.includes('pdf') || mimeType.includes('document')) return <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />;
    return <File className="w-5 h-5 sm:w-6 sm:h-6 text-text-muted" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getSourceColors = (source: string) => {
    const colors: Record<string, string> = {
      PROJETO: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      DEMANDA: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      OS: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
    return colors[source] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getSourceIcons = (source: string) => {
    const icons: Record<string, string> = {
      PROJETO: '📁',
      DEMANDA: '📌',
      OS: '📋',
    };
    return icons[source] || '📄';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-full max-w-full shadow-glass rounded-button bg-background-glass backdrop-blur-glass max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-text-primary">{title}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-background-glass-hover rounded"
          >
            <X className="w-5 h-5 text-text-muted" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Informações da Entidade */}
          <div className="space-y-4">
            <div className="bg-background-glass-hover p-4 rounded-glass">
              <h4 className="font-bold text-text-primary mb-2">
                Informações da {entityType === 'demand' ? 'Demanda' : entityType === 'project' ? 'Projeto' : 'OS'}
              </h4>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-bold">Nome:</span> {entityInfo?.name || 'N/A'}
                </div>
                {entityInfo?.status && (
                  <div>
                    <span className="font-bold">Status:</span> {entityInfo.status}
                  </div>
                )}
                {entityInfo?.priority && (
                  <div>
                    <span className="font-bold">Prioridade:</span> {entityInfo.priority}
                  </div>
                )}
                {entityInfo?.client && (
                  <div>
                    <span className="font-bold">Cliente:</span> {entityInfo.client}
                  </div>
                )}
                {entityInfo?.description && (
                  <div>
                    <span className="font-bold">Descrição:</span> {entityInfo.description}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Arquivos Relacionados */}
          <div>
            <div className="border rounded-glass bg-background-glass backdrop-blur-glass">
              {/* Cabeçalho com ações */}
              <div className="flex flex-col gap-2 p-3 border-b bg-background-glass-hover">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-text-primary">Arquivos Relacionados</h3>
                    <p className="text-xs text-text-muted mt-0.5">
                      Visualize arquivos da {entityType === 'demand' ? 'Demanda' : entityType === 'project' ? 'Projeto' : 'OS'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        const input = document.getElementById('file-upload') as HTMLInputElement;
                        input?.click();
                      }}
                      className="flex items-center space-x-1 px-3 py-2 text-sm bg-blue-500 text-white rounded-button hover:bg-gradient-to-r from-primary to-secondary shadow-glass"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Upload</span>
                    </button>
                    <button
                      onClick={() => {
                        // TODO: Implementar criação de pasta
                        toast.info('Funcionalidade de criar pasta em desenvolvimento');
                      }}
                      className="flex items-center space-x-1 px-3 py-2 text-sm bg-green-500 text-white rounded-button hover:bg-green-600 shadow-glass"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Nova Pasta</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Upload Area */}
              <div className="p-4 border-b">
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                    dragActive 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600 mb-2 text-sm">
                    Arraste arquivos aqui ou clique para selecionar
                  </p>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                  />
                </div>

                {/* Selected Files */}
                {selectedFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Arquivos Selecionados:</h4>
                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <div className="flex items-center gap-2">
                            {getFileIcon({ ...file, isFolder: false, mimeType: file.type } as any)}
                            <span className="text-sm text-gray-700">{file.name}</span>
                            <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                          </div>
                          <button
                            onClick={() => removeSelectedFile(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={uploadFiles}
                      disabled={uploading}
                      className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {uploading ? 'Enviando...' : 'Enviar Arquivos'}
                    </button>
                  </div>
                )}
              </div>

              {/* Lista de arquivos */}
              <div className="max-h-96 overflow-y-auto">
                {loading ? (
                  <div className="p-4">
                    <div className="animate-pulse space-y-2">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-background-glass-hover rounded"></div>
                          <div className="w-32 h-4 bg-background-glass-hover rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : files.length === 0 ? (
                  <div className="p-8 text-center text-text-muted">
                    <Folder className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">Nenhum arquivo encontrado</p>
                    <p className="text-xs text-text-muted mt-1">
                      Faça upload de arquivos ou crie uma pasta
                    </p>
                  </div>
                ) : (
                  <div className="p-2">
                    {files.map((file) => (
                      <div key={file.id} className="group flex flex-col sm:flex-row items-start sm:items-center justify-between py-3 px-3 sm:px-4 hover:bg-background-glass-hover rounded-glass border border-border-glass mb-2 shadow-glass hover:shadow-md transition-all duration-300 gap-3">
                        <div className="flex items-start space-x-3 flex-1 min-w-0 w-full sm:w-auto">
                          <div className="flex-shrink-0 mt-0.5">
                            {getFileIcon(file)}
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-sm font-bold text-text-primary truncate block max-w-full">
                              {file.originalName}
                            </span>
                            
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border flex-shrink-0 ${getSourceColors(file.source)}`}>
                                {getSourceIcons(file.source)} {file.source}
                              </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border flex-shrink-0 ${
                                file.isPublic 
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                  : 'bg-red-500/20 text-red-400 border-red-500/30'
                              }`}>
                                {file.isPublic ? '🌐 Público' : '🔒 Privado'}
                              </span>
                              {!file.editable && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 flex-shrink-0">
                                  🔒 Leitura
                                </span>
                              )}
                            </div>
                            
                            {!file.isFolder && (
                              <span className="text-xs text-text-muted mt-1">
                                {formatFileSize(file.fileSize)} • {file.uploader.name}
                              </span>
                            )}
                            <span className="text-xs text-text-muted">
                              {new Date(file.createdAt).toLocaleDateString('pt-BR')}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 w-full sm:w-auto flex-shrink-0">
                          {!file.isFolder && (
                            <button
                              onClick={() => downloadFile(file)}
                              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-bold bg-gradient-to-r from-primary to-secondary text-white rounded-glass hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-background-dark transition-all duration-300 shadow-glass hover:shadow-md"
                              title="Baixar arquivo"
                            >
                              <Download className="w-4 h-4 flex-shrink-0" />
                              <span className="hidden sm:inline">Download</span>
                            </button>
                          )}
                          
                          {(user?.role === 'ADMIN' || user?.role === 'GESTOR') && (
                            <button
                              onClick={() => changeFileVisibility(file, !file.isPublic)}
                              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-bold rounded-glass focus:outline-none focus:ring-2 focus:ring-offset-background-dark transition-all duration-300 shadow-glass hover:shadow-md ${
                                file.isPublic 
                                  ? 'bg-orange-600 text-white hover:bg-orange-700 focus:ring-orange-500' 
                                  : 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                              }`}
                              title={file.isPublic ? 'Tornar privado' : 'Tornar público'}
                            >
                              {file.isPublic ? <EyeOff className="w-4 h-4 flex-shrink-0" /> : <Eye className="w-4 h-4 flex-shrink-0" />}
                              <span className="hidden sm:inline">{file.isPublic ? 'Privado' : 'Público'}</span>
                            </button>
                          )}
                          
                          <button
                            onClick={() => deleteFile(file.id)}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-bold bg-red-600 text-white rounded-glass hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-background-dark transition-all duration-300 shadow-glass hover:shadow-md"
                            title="Excluir arquivo"
                          >
                            <Trash2 className="w-4 h-4 flex-shrink-0" />
                            <span className="hidden sm:inline">Excluir</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-border-glass">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Fechar
          </button>
          <button
            onClick={() => onSave(files.map(f => f.id))}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFileManagerModal;
